import './offstripe-scroll.js';

import {
  getScrollTop,
  getScrollContainer,
  getScrollEventTarget,
  scrollContainerMediaQuery,
} from '@theme/scroll-container';

import {
  StandardEvents,
} from '@shopify/events';


class OffstripeHeader extends HTMLElement {
  #section = null;
  #menu = null;
  #openButton = null;
  #closeButton = null;

  #darkLogo = null;
  #boneLogo = null;
  #topTone = 'dark';

  #scrollTarget = null;
  #scrollRafId = null;

  #layoutContainer = null;
  #layoutResizeObserver = null;
  #layoutRafId = null;

  #closeTimer = null;
  #isClosing = false;


  connectedCallback() {
    this.#section = this.closest(
      '.offstripe-header-section'
    );

    this.#menu = this.querySelector(
      '[data-offstripe-menu-overlay]'
    );

    this.#openButton = this.querySelector(
      '[data-offstripe-menu-open]'
    );

    this.#closeButton = this.querySelector(
      '[data-offstripe-menu-close]'
    );

    this.#darkLogo = this.querySelector(
      '[data-offstripe-logo-dark]'
    );

    this.#boneLogo = this.querySelector(
      '[data-offstripe-logo-bone]'
    );


    this.#openButton?.addEventListener(
      'click',
      this.#openMenu
    );

    this.#closeButton?.addEventListener(
      'click',
      this.#closeMenu
    );


    document.addEventListener(
      'keydown',
      this.#handleKeydown
    );

    document.addEventListener(
      'shopify:section:load',
      this.#handleSectionLoad
    );

    document.addEventListener(
      StandardEvents.cartLinesUpdate,
      this.#handleCartUpdate
    );


    window.addEventListener(
      'pageshow',
      this.#handlePageShow
    );

    window.addEventListener(
      'resize',
      this.#scheduleLayoutFrame,
      {
        passive: true,
      }
    );

    window.addEventListener(
      'orientationchange',
      this.#scheduleLayoutFrame,
      {
        passive: true,
      }
    );


    scrollContainerMediaQuery.addEventListener(
      'change',
      this.#handleBreakpointChange
    );


    this.#readTopTone();

    this.#bindScrollTarget();
    this.#bindLayoutFrame();

    this.#updateScrollState();

    requestAnimationFrame(() => {
      this.#updateScrollState();
      this.#syncLayoutFrame();
    });
  }


  disconnectedCallback() {
    this.#openButton?.removeEventListener(
      'click',
      this.#openMenu
    );

    this.#closeButton?.removeEventListener(
      'click',
      this.#closeMenu
    );


    document.removeEventListener(
      'keydown',
      this.#handleKeydown
    );

    document.removeEventListener(
      'shopify:section:load',
      this.#handleSectionLoad
    );

    document.removeEventListener(
      StandardEvents.cartLinesUpdate,
      this.#handleCartUpdate
    );


    window.removeEventListener(
      'pageshow',
      this.#handlePageShow
    );

    window.removeEventListener(
      'resize',
      this.#scheduleLayoutFrame
    );

    window.removeEventListener(
      'orientationchange',
      this.#scheduleLayoutFrame
    );


    scrollContainerMediaQuery.removeEventListener(
      'change',
      this.#handleBreakpointChange
    );


    this.#unbindScrollTarget();
    this.#unbindLayoutFrame();


    if (this.#scrollRafId !== null) {
      cancelAnimationFrame(
        this.#scrollRafId
      );

      this.#scrollRafId = null;
    }


    if (this.#layoutRafId !== null) {
      cancelAnimationFrame(
        this.#layoutRafId
      );

      this.#layoutRafId = null;
    }


    if (this.#closeTimer !== null) {
      window.clearTimeout(
        this.#closeTimer
      );

      this.#closeTimer = null;
    }


    this.#menu?.removeEventListener(
      'animationend',
      this.#handleCloseAnimationEnd
    );


    document.documentElement.classList.remove(
      'offstripe-menu-is-open'
    );
  }


  /* =========================================================
     HEADER TONE
  ========================================================= */

  #readTopTone = () => {
    const toneSource = document.querySelector(
      '[data-offstripe-header-tone]'
    );

    const tone =
      toneSource?.dataset.offstripeHeaderTone;

    this.#topTone =
      tone === 'bone' ? 'bone' : 'dark';
  };


  #applyHeaderTone = (isScrolled) => {
    if (!this.#section) return;

    const effectiveTone =
      isScrolled ? 'dark' : this.#topTone;

    this.#section.style.color =
      effectiveTone === 'bone'
        ? 'var(--offstripe-bone)'
        : 'var(--offstripe-dark)';


    const useBoneLogo =
      effectiveTone === 'bone' &&
      this.#boneLogo !== null;


    if (this.#darkLogo) {
      this.#darkLogo.hidden = useBoneLogo;
    }

    if (this.#boneLogo) {
      this.#boneLogo.hidden = !useBoneLogo;
    }
  };


  /* =========================================================
     HORIZON LAYOUT FRAME
  ========================================================= */

  #bindLayoutFrame = () => {
    this.#unbindLayoutFrame();

    const container =
      getScrollContainer();

    if (!(container instanceof HTMLElement)) {
      this.#scheduleLayoutFrame();
      return;
    }

    this.#layoutContainer =
      container;


    if ('ResizeObserver' in window) {
      this.#layoutResizeObserver =
        new ResizeObserver(
          this.#scheduleLayoutFrame
        );

      this.#layoutResizeObserver.observe(
        this.#layoutContainer
      );
    }


    this.#scheduleLayoutFrame();
  };


  #unbindLayoutFrame = () => {
    this.#layoutResizeObserver?.disconnect();

    this.#layoutResizeObserver = null;
    this.#layoutContainer = null;
  };


  #scheduleLayoutFrame = () => {
    if (this.#layoutRafId !== null) {
      return;
    }

    this.#layoutRafId =
      requestAnimationFrame(() => {
        this.#layoutRafId = null;

        this.#syncLayoutFrame();
      });
  };


  #syncLayoutFrame = () => {
    if (!this.#section) return;

    const container =
      this.#layoutContainer ||
      getScrollContainer();

    if (!(container instanceof HTMLElement)) {
      return;
    }


    const rect =
      container.getBoundingClientRect();


    /*
     * clientWidth excludes the vertical scrollbar.
     *
     * This is the important difference between
     * Horizon's actual page viewport and window.innerWidth.
     */
    const frameWidth =
      container.clientWidth;


    const frameLeft =
      rect.left +
      container.clientLeft;


    if (
      !Number.isFinite(frameWidth) ||
      !Number.isFinite(frameLeft) ||
      frameWidth <= 0
    ) {
      return;
    }


    /*
     * The fixed header must use the same horizontal
     * frame as the content inside .page-wrapper.
     */
    this.#section.style.left =
      `${frameLeft}px`;

    this.#section.style.width =
      `${frameWidth}px`;


    /*
     * The menu overlay is also position: fixed,
     * so it needs the same frame explicitly.
     *
     * Its Bone background still fills the complete
     * visible page area; only the browser scrollbar
     * is excluded.
     */
    if (this.#menu) {
      this.#menu.style.left =
        `${frameLeft}px`;

      this.#menu.style.right =
        'auto';

      this.#menu.style.width =
        `${frameWidth}px`;
    }
  };


  /* =========================================================
     SCROLL
  ========================================================= */

  #bindScrollTarget = () => {
    this.#unbindScrollTarget();

    this.#scrollTarget =
      getScrollEventTarget();

    this.#scrollTarget.addEventListener(
      'scroll',
      this.#handleScroll,
      {
        passive: true,
      }
    );
  };


  #unbindScrollTarget = () => {
    if (!this.#scrollTarget) return;

    this.#scrollTarget.removeEventListener(
      'scroll',
      this.#handleScroll
    );

    this.#scrollTarget = null;
  };


  #handleScroll = () => {
    if (this.#scrollRafId !== null) return;

    this.#scrollRafId =
      requestAnimationFrame(() => {
        this.#scrollRafId = null;

        this.#updateScrollState();
      });
  };


  #updateScrollState = () => {
    if (!this.#section) return;

    const isScrolled =
      getScrollTop() > 24;

    this.#section.classList.toggle(
      'offstripe-header-section--scrolled',
      isScrolled
    );

    this.#applyHeaderTone(isScrolled);
  };


  #handleBreakpointChange = () => {
    this.#bindScrollTarget();
    this.#bindLayoutFrame();

    this.#updateScrollState();
  };


  #handlePageShow = () => {
    this.#readTopTone();

    this.#scheduleLayoutFrame();

    requestAnimationFrame(
      this.#updateScrollState
    );
  };


  #handleSectionLoad = () => {
    this.#readTopTone();

    this.#scheduleLayoutFrame();

    requestAnimationFrame(
      this.#updateScrollState
    );
  };


  /* =========================================================
     LIVE CART COUNT
  ========================================================= */

  #handleCartUpdate = (event) => {
    event.promise
      ?.then(({ cart }) => {
        const cartCount =
          cart?.totalQuantity;

        if (
          typeof cartCount !== 'number'
        ) {
          return;
        }

        this.#updateCartCount(
          cartCount
        );
      })
      .catch((error) => {
        if (
          error?.name !== 'AbortError'
        ) {
          console.warn(
            '[OFFSTRIPE header] Cart update failed.',
            error
          );
        }
      });
  };


  #updateCartCount = (cartCount) => {
    const labels =
      this.querySelectorAll(
        '[data-offstripe-cart-label]'
      );

    const links =
      this.querySelectorAll(
        '[data-offstripe-cart-link]'
      );


    labels.forEach((label) => {
      label.textContent =
        cartCount > 0
          ? `CART ${cartCount}`
          : 'CART';
    });


    links.forEach((link) => {
      link.setAttribute(
        'aria-label',
        cartCount > 0
          ? `Cart, ${cartCount} ${
              cartCount === 1
                ? 'item'
                : 'items'
            }`
          : 'Cart'
      );
    });
  };


  /* =========================================================
     MENU OPEN
  ========================================================= */

  #openMenu = () => {
    if (!this.#menu) return;


    if (this.#closeTimer !== null) {
      window.clearTimeout(
        this.#closeTimer
      );

      this.#closeTimer = null;
    }


    this.#menu.removeEventListener(
      'animationend',
      this.#handleCloseAnimationEnd
    );


    this.#isClosing = false;

    this.#menu.classList.remove(
      'offstripe-menu-overlay--closing'
    );


    /*
     * Re-read the frame immediately before opening.
     */
    this.#syncLayoutFrame();

    this.#menu.hidden = false;


    document.documentElement.classList.add(
      'offstripe-menu-is-open'
    );


    this.#openButton?.setAttribute(
      'aria-expanded',
      'true'
    );
  };


  /* =========================================================
     MENU CLOSE
  ========================================================= */

  #closeMenu = () => {
    if (!this.#menu) return;
    if (this.#menu.hidden) return;
    if (this.#isClosing) return;


    this.#isClosing = true;


    this.#openButton?.setAttribute(
      'aria-expanded',
      'false'
    );


    this.#menu.classList.add(
      'offstripe-menu-overlay--closing'
    );


    this.#menu.addEventListener(
      'animationend',
      this.#handleCloseAnimationEnd
    );


    this.#closeTimer =
      window.setTimeout(
        this.#finishClose,
        340
      );
  };


  #handleCloseAnimationEnd = (event) => {
    if (!this.#menu) return;

    if (
      event.target !== this.#menu
    ) {
      return;
    }

    if (
      event.animationName !==
      'offstripe-menu-overlay-close'
    ) {
      return;
    }


    this.#finishClose();
  };


  #finishClose = () => {
    if (!this.#menu) return;


    if (this.#closeTimer !== null) {
      window.clearTimeout(
        this.#closeTimer
      );

      this.#closeTimer = null;
    }


    this.#menu.removeEventListener(
      'animationend',
      this.#handleCloseAnimationEnd
    );


    this.#menu.hidden = true;

    this.#menu.classList.remove(
      'offstripe-menu-overlay--closing'
    );


    document.documentElement.classList.remove(
      'offstripe-menu-is-open'
    );


    this.#isClosing = false;


    this.#openButton?.focus();
  };


  /* =========================================================
     KEYBOARD
  ========================================================= */

  #handleKeydown = (event) => {
    if (event.key !== 'Escape') return;

    if (
      !this.#menu ||
      this.#menu.hidden
    ) {
      return;
    }

    this.#closeMenu();
  };
}


if (
  !customElements.get(
    'offstripe-header'
  )
) {
  customElements.define(
    'offstripe-header',
    OffstripeHeader
  );
}